<template>
  <div class="leprosy-section">
    <div class="section-title" @click="toggleSection('summary')">
      <span>{{ $t('whole-slide-result') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.summary }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.summary" class="section-content">
      <div class="columns is-mobile is-multiline m-0">
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered leprosy-status-box" :class="statusClass">
            <div class="has-text-grey">{{ $t('leprosy-afb-status') }}</div>
            <div class="is-size-5 has-text-weight-bold">{{ result.summary.status }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('total-afb') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-primary">{{ result.summary.totalAfbCount }}</div>
          </div>
        </div>
        <div class="column is-6 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('ai-confidence') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-info">{{ formatPercent(result.summary.aiConfidence) }}</div>
          </div>
        </div>
      </div>

      <div class="leprosy-term-card">
        <div class="leprosy-term-header">
          <button class="algorithm-term-toggle" type="button" @click="toggleTermRow('afb')">
            <span class="term-arrow">{{ isTermRowExpanded('afb') ? '▼' : '▶' }}</span>
          </button>
          <label class="switch is-small leprosy-term-annotation-switch">
            <input
              type="checkbox"
              :checked="isOverlayVisibleForType('afb')"
              data-testid="leprosy-overlay-toggle-afb"
              @change="updateOverlayVisibility('afb', $event.target.checked)"
            >
            <span class="switch-slider"></span>
          </label>
          <div class="leprosy-term-title">
            <span class="dot afb-dot"></span>
            <span>{{ $t('afb-bacilli') }}</span>
          </div>
        </div>

        <div class="leprosy-term-summary">
          <div class="leprosy-term-summary-row">
            <span>{{ $t('count') }}</span>
            <strong>{{ result.summary.totalAfbCount }}</strong>
          </div>
          <div class="leprosy-term-summary-row">
            <span>{{ $t('mean-confidence') }}</span>
            <strong>{{ formatPercent(meanConfidence('afb')) }}</strong>
          </div>
        </div>

        <div class="leprosy-term-controls">
          <div class="leprosy-term-threshold">
            <div class="leprosy-threshold-label">
              <button
                class="leprosy-threshold-toggle"
                type="button"
                data-testid="leprosy-threshold-toggle-afb"
                @click="toggleThresholdControls('afb')"
              >
                <span class="term-arrow">{{ isThresholdControlsExpanded('afb') ? '▼' : '▶' }}</span>
                <span>{{ $t('confidence-threshold') }}</span>
              </button>
              <div class="leprosy-threshold-actions">
                <span class="leprosy-heatmap-label">{{ $t('heatmap') }}</span>
                <label class="switch is-small leprosy-term-heatmap-switch">
                  <input
                    type="checkbox"
                    :checked="isHeatmapVisibleForType('afb')"
                    data-testid="leprosy-heatmap-toggle-afb"
                    @change="updateHeatmapVisibility('afb', $event.target.checked)"
                  >
                  <span class="switch-slider"></span>
                </label>
                <span class="leprosy-heatmap-label">{{ $t('confidence') }}</span>
                <label class="switch is-small leprosy-term-confidence-label-switch">
                  <input
                    type="checkbox"
                    :checked="isConfidenceLabelVisibleForType('afb')"
                    data-testid="leprosy-confidence-label-toggle-afb"
                    @change="updateConfidenceLabelVisibility('afb', $event.target.checked)"
                  >
                  <span class="switch-slider"></span>
                </label>
                <span class="leprosy-threshold-value">{{ formatDecimal(thresholdForType('afb')) }}</span>
              </div>
            </div>
            <input
              v-if="isThresholdControlsExpanded('afb')"
              class="slider is-fullwidth"
              data-testid="leprosy-threshold-slider-afb"
              type="range"
              min="0"
              max="1"
              step="0.01"
              :value="thresholdForType('afb')"
              @input="updateThreshold('afb', $event.target.value)"
            >
          </div>
        </div>

        <div v-if="isTermRowExpanded('afb')" class="leprosy-term-detections">
          <div v-if="paginatedDetectionsForType('afb').length" class="leprosy-detection-previews">
            <div
              v-for="detection in paginatedDetectionsForType('afb')"
              :key="detection.id"
              class="leprosy-detection-preview"
            >
              <annotation-preview
                :annot="annotationForDetection(detection)"
                :size="72"
                color="ff3860"
                :terms="allTerms"
                :images="images"
                :show-image-info="false"
                :same-view-on-click="true"
                @updateTermsOrTracks="$emit('updateTermsOrTracks', annotationForDetection(detection))"
                @centerView="$emit('centerView', { annot: annotationForDetection(detection), sameView: true })"
                @deletion="$emit('deletion', annotationForDetection(detection))"
                @select="$emit('select', $event)"
              />
              <div class="leprosy-detection-confidence">{{ formatDecimal(detection.confidence) }}</div>
            </div>
          </div>
          <div v-else class="has-text-centered has-text-grey leprosy-detection-empty">
            {{ $t('no-detections-current-threshold') }}
          </div>
          <b-pagination
            v-if="detectionsForType('afb').length > detectionPageSize"
            class="leprosy-detection-pagination"
            size="is-small"
            order="is-centered"
            :total="detectionsForType('afb').length"
            :current.sync="detectionPages.afb"
            :per-page="detectionPageSize"
          />
        </div>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('grading')">
      <span>{{ $t('afb-grading') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.grading }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.grading" class="section-content">
      <div class="box p-3 scoring-details-box">
        <table class="table is-fullwidth is-size-7 centered-table">
          <tbody>
            <tr>
              <td>{{ $t('ai-total-afb') }}</td>
              <td class="has-text-centered">{{ result.summary.rawTotalAfbCount }}</td>
            </tr>
            <tr>
              <td>{{ $t('final-total-afb') }}</td>
              <td class="has-text-centered has-text-weight-bold">{{ result.report.finalTotalAfbCount }}</td>
            </tr>
            <tr>
              <td>{{ $t('final-afb-grading') }}</td>
              <td class="has-text-centered has-text-weight-bold">{{ result.report.finalGrading }}</td>
            </tr>
            <!-- <tr>
              <td>{{ $t('targeted-hotspot-image') }}</td>
              <td class="has-text-centered">{{ result.summary.hotspotImageStatus }}</td>
            </tr> -->
          </tbody>
        </table>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('report')">
      <span>{{ $t('report') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.report }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.report" class="section-content report-form">
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('leprosy-afb-status') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.detectionStatus" @change="syncReportSummary">
                <option value="Detected">{{ $t('detected') }}</option>
                <option value="No AFB Seen">{{ $t('no-afb-seen') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('final-total-afb') }}</label></div>
        <div class="field-body">
          <div class="control report-control-fill">
            <input class="input is-small" type="number" min="0" step="1" v-model.number="result.report.finalTotalAfbCount" @input="syncReportSummary">
          </div>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('final-afb-grading') }}</label></div>
        <div class="field-body">
          <div class="control">
            <div class="select is-small is-fullwidth">
              <select v-model="result.report.finalGrading" @change="syncReportSummary">
                <option value="No AFB Seen">{{ $t('no-afb-seen') }}</option>
                <option value="Scanty">Scanty</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('verified-by') }}</label></div>
        <div class="field-body">
          <div class="control report-control-fill">
            <input class="input is-small" type="text" v-model="result.report.verifiedBy">
          </div>
        </div>
      </div>
      <!-- <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('date-reported') }}</label></div>
        <div class="field-body">
          <div class="control report-control-fill">
            <input class="input is-small" type="date" v-model="result.report.dateReported">
          </div>
        </div>
      </div> -->
      <div class="field is-small mb-2">
        <label class="label">{{ $t('remarks-notes') }}</label>
        <textarea class="textarea is-small" rows="3" v-model="result.report.remarks"></textarea>
      </div>
      <button class="button is-primary is-small is-fullwidth mt-3">{{ $t('confirm-results') }}</button>
    </div>

    <!-- <div class="section-title" @click="toggleSection('printedReport')">
      <span>{{ $t('printed-report-preview') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.printedReport }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.printedReport" class="section-content">
      <div class="printed-report-preview">
        <div><strong>{{ $t('report-of-smear-grading') }}:</strong> {{ result.report.finalGrading }}</div>
        <div><strong>{{ $t('final-total-afb') }}:</strong> {{ result.report.finalTotalAfbCount }}</div>
        <div><strong>{{ $t('verification-by-competent-personnel') }}:</strong> {{ result.report.verifiedBy || 'N/A' }}</div>
        <div><strong>{{ $t('date-reported') }}:</strong> {{ result.report.dateReported || 'N/A' }}</div>
      </div>
    </div> -->

    <div class="section-title" @click="toggleSection('activity')">
      <span>{{ $t('activity-log') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.activity }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.activity" class="section-content">
      <div v-if="result.activityLog.length" class="activity-log-list">
        <div v-for="(entry, entryIndex) in result.activityLog" :key="entryIndex" class="activity-log-entry">
          {{ entry }}
        </div>
      </div>
      <div v-else class="has-text-grey has-text-centered">{{ $t('no-activity-recorded') }}</div>
    </div>
  </div>
</template>

<script>
import { Annotation, Cytomine } from '@/api';
import AnnotationPreview from '@/components/annotations/AnnotationPreview';
import { includedLeprosyDetections, leprosyGradingForCount, recomputeLeprosyResult } from './ai-models/leprosy';
import {
  activeTermHeatmapPoints,
  buildTermHeatmapLayers,
  visibilityByTerm
} from './ai-models/term-visualization';

const HEATMAP_GRADIENTS = {
  afb: ['rgba(255, 221, 225, 0)', '#ffdce2', '#ff9aaa', '#ff3860', '#8b1026']
};
const HEATMAP_Z_INDEX_BY_TYPE = {
  afb: 2
};
const LEPROSY_TERM_TYPES = ['afb'];

export default {
  name: 'leprosy-result-panel',
  components: {
    AnnotationPreview
  },
  props: {
    result: {
      type: Object,
      required: true
    },
    project: Object,
    index: String,
    allTerms: {
      type: Array,
      default: () => []
    },
    ontologyTerms: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Array,
      default: () => []
    },
    activeSliceId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      expandedTermRows: {
        afb: false
      },
      expandedThresholdControls: {
        afb: false
      },
      detectionPageSize: 50,
      detectionPages: {
        afb: 1
      }
    };
  },
  mounted() {
    this.ensureInitialVisualizationState();
    this.emitVisualization();
  },
  computed: {
    statusClass() {
      return this.result.summary.status === 'Detected' ? 'is-detected' : 'is-not-detected';
    },
    includedDetections() {
      return includedLeprosyDetections(this.result);
    },
    activeOverlayPoints() {
      const visibleByType = this.overlayVisibleByType();
      const annotations = this.result.overlay.annotations || this.result.overlay.points || [];
      const confidenceLabelsByType = this.confidenceLabelsVisibleByType();
      return annotations
        .filter(point => visibleByType[this.normalizedDetectionType(point)])
        .map(point => ({
          ...point,
          showConfidenceLabel: confidenceLabelsByType[this.normalizedDetectionType(point)] !== false
        }));
    },
    activeHeatmapPoints() {
      this.heatmapVisibleByType();
      return activeTermHeatmapPoints({
        heatmap: this.result.heatmap,
        normalizePointType: this.normalizedHeatmapPointType
      });
    },
    activeHeatmapLayers() {
      this.heatmapVisibleByType();
      return buildTermHeatmapLayers({
        termTypes: LEPROSY_TERM_TYPES,
        heatmap: this.result.heatmap,
        gradients: HEATMAP_GRADIENTS,
        zIndexByType: HEATMAP_Z_INDEX_BY_TYPE,
        idForType: type => `leprosy-heatmap-${type}`,
        normalizePointType: this.normalizedHeatmapPointType
      });
    }
  },
  methods: {
    toggleSection(sectionName) {
      if (this.result.sectionCollapsed && typeof this.result.sectionCollapsed[sectionName] !== 'undefined') {
        this.result.sectionCollapsed[sectionName] = !this.result.sectionCollapsed[sectionName];
      }
    },
    ensureThresholds() {
      const hasConfidenceThreshold = this.result.thresholds?.confidence !== undefined && this.result.thresholds?.confidence !== null;
      if (!this.result.thresholds) {
        this.$set(this.result, 'thresholds', { confidence: 0, byType: {} });
      }
      if (!this.result.thresholds.byType) {
        this.$set(this.result.thresholds, 'byType', {});
      }
      const fallback = this.clampThreshold(this.result.thresholds.confidence);
      if (this.result.thresholds.byType.afb === undefined) {
        this.$set(this.result.thresholds.byType, 'afb', hasConfidenceThreshold ? fallback : 0);
      }
    },
    ensureInitialVisualizationState() {
      this.ensureThresholds();
      this.overlayVisibleByType();
      this.heatmapVisibleByType();
      this.confidenceLabelsVisibleByType();
    },
    thresholdForType(type) {
      this.ensureThresholds();
      return this.clampThreshold(this.result.thresholds.byType[type]);
    },
    updateThreshold(type, value) {
      this.ensureThresholds();
      this.$set(this.result.thresholds.byType, type, this.clampThreshold(value));
      this.resetDetectionPage(type);
      this.recomputeAndEmit();
    },
    ensureTypedVisibility(sectionName, defaultVisible) {
      if (!this.result[sectionName]) {
        this.$set(this.result, sectionName, { visible: defaultVisible, byTypeVisible: {}, points: [] });
      }
      const section = this.result[sectionName];
      if (!section.byTypeVisible) {
        this.$set(section, 'byTypeVisible', {});
      }
      LEPROSY_TERM_TYPES.forEach(type => {
        if (section.byTypeVisible[type] === undefined) {
          this.$set(section.byTypeVisible, type, section.visible !== false);
        }
      });
      return section.byTypeVisible;
    },
    overlayVisibleByType() {
      return this.ensureTypedVisibility('overlay', false);
    },
    heatmapVisibleByType() {
      return this.ensureTypedVisibility('heatmap', false);
    },
    confidenceLabelsVisibleByType() {
      if (!this.result.overlay) {
        this.$set(this.result, 'overlay', { visible: true, byTypeVisible: {}, points: [] });
      }
      if (!this.result.overlay.showConfidenceLabelsByType) {
        this.$set(this.result.overlay, 'showConfidenceLabelsByType', {});
      }
      LEPROSY_TERM_TYPES.forEach(type => {
        if (this.result.overlay.showConfidenceLabelsByType[type] === undefined) {
          this.$set(this.result.overlay.showConfidenceLabelsByType, type, false);
        }
      });
      return visibilityByTerm(LEPROSY_TERM_TYPES, this.result.overlay, false, 'showConfidenceLabelsByType');
    },
    isOverlayVisibleForType(type) {
      return this.overlayVisibleByType()[type] === true;
    },
    isHeatmapVisibleForType(type) {
      return this.heatmapVisibleByType()[type] === true;
    },
    isConfidenceLabelVisibleForType(type) {
      return this.confidenceLabelsVisibleByType()[type] !== false;
    },
    toggleThresholdControls(type) {
      this.$set(this.expandedThresholdControls, type, !this.expandedThresholdControls[type]);
    },
    isThresholdControlsExpanded(type) {
      return this.expandedThresholdControls[type] === true;
    },
    updateHeatmapVisibility(type, visible) {
      this.heatmapVisibleByType();
      this.$set(this.result.heatmap.byTypeVisible, type, visible);
      this.result.heatmap.visible = Object.values(this.result.heatmap.byTypeVisible).some(Boolean);
      this.recomputeAndEmit();
    },
    updateConfidenceLabelVisibility(type, visible) {
      this.confidenceLabelsVisibleByType();
      this.$set(this.result.overlay.showConfidenceLabelsByType, type, visible);
      this.emitVisualization();
    },
    updateOverlayVisibility(type, visible) {
      this.overlayVisibleByType();
      this.$set(this.result.overlay.byTypeVisible, type, visible);
      this.result.overlay.visible = Object.values(this.result.overlay.byTypeVisible).some(Boolean);
      if (!visible) {
        this.heatmapVisibleByType();
        this.$set(this.result.heatmap.byTypeVisible, type, false);
        this.result.heatmap.visible = Object.values(this.result.heatmap.byTypeVisible).some(Boolean);
      }
      this.recomputeAndEmit();
    },
    clampThreshold(value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return 0;
      }
      return Math.max(0, Math.min(1, numberValue));
    },
    toggleTermRow(type) {
      this.$set(this.expandedTermRows, type, !this.expandedTermRows[type]);
      this.ensureDetectionPage(type);
    },
    isTermRowExpanded(type) {
      return this.expandedTermRows[type] === true;
    },
    recomputeAndEmit() {
      Object.assign(this.result, recomputeLeprosyResult(this.result));
      this.emitVisualization();
    },
    emitVisualization() {
      this.emitSettings();
      this.emitHeatmap();
      this.emitOverlay();
    },
    emitSettings() {
      this.$emit('settingsChange', {
        showAllAnnotations: this.result.showAllAnnotations,
        thresholds: {
          confidence: this.result.thresholds?.confidence,
          byType: {
            ...(this.result.thresholds?.byType || {})
          }
        },
        heatmap: {
          visible: this.result.heatmap?.visible,
          opacity: this.result.heatmap?.opacity,
          radius: this.result.heatmap?.radius,
          blur: this.result.heatmap?.blur,
          byTypeVisible: {
            ...(this.result.heatmap?.byTypeVisible || {})
          }
        },
        overlay: {
          visible: this.result.overlay?.visible,
          opacity: this.result.overlay?.opacity,
          byTypeVisible: {
            ...(this.result.overlay?.byTypeVisible || {})
          },
          showConfidenceLabelsByType: {
            ...(this.result.overlay?.showConfidenceLabelsByType || {})
          }
        }
      });
    },
    emitHeatmap() {
      this.$emit('heatmapChange', {
        visible: this.result.showAllAnnotations && this.result.heatmap.visible && this.activeHeatmapPoints.length > 0,
        opacity: this.result.heatmap.opacity,
        radius: this.result.heatmap.radius,
        blur: this.result.heatmap.blur,
        byTypeVisible: this.result.heatmap.byTypeVisible,
        points: this.activeHeatmapPoints,
        layers: this.activeHeatmapLayers,
        zIndex: 2
      });
    },
    emitOverlay() {
      const annotations = this.activeOverlayPoints;
      this.$emit('overlayChange', {
        visible: this.result.showAllAnnotations && this.result.overlay.visible && annotations.length > 0,
        opacity: this.result.overlay.opacity,
        byTypeVisible: this.result.overlay.byTypeVisible,
        annotations,
        points: annotations,
        zIndex: 3
      });
    },
    syncReportSummary() {
      this.result.report.finalTotalAfbCount = Number(this.result.report.finalTotalAfbCount) || 0;
      this.result.report.finalGrading = leprosyGradingForCount(this.result.report.finalTotalAfbCount);
      this.result.report.detectionStatus = this.result.report.finalTotalAfbCount > 0 ? 'Detected' : 'No AFB Seen';
      this.result.summary.status = this.result.report.detectionStatus;
      this.result.summary.totalAfbCount = this.result.report.finalTotalAfbCount;
      this.result.summary.grading = this.result.report.finalGrading;
    },
    detectionsForType(type) {
      const derivedDetections = this.result.derived?.sortedIncludedByType?.[type];
      if (Array.isArray(derivedDetections)) {
        return derivedDetections;
      }
      return this.includedDetections
        .filter(detection => this.normalizedDetectionType(detection) === type)
        .slice()
        .sort((a, b) => b.confidence - a.confidence);
    },
    ensureDetectionPage(type) {
      if (!this.detectionPages[type]) {
        this.$set(this.detectionPages, type, 1);
      }
    },
    resetDetectionPage(type) {
      this.$set(this.detectionPages, type, 1);
    },
    paginatedDetectionsForType(type) {
      this.ensureDetectionPage(type);
      const detections = this.detectionsForType(type);
      const maxPage = Math.max(1, Math.ceil(detections.length / this.detectionPageSize));
      const currentPage = Math.min(this.detectionPages[type], maxPage);
      if (currentPage !== this.detectionPages[type]) {
        this.$set(this.detectionPages, type, currentPage);
      }
      const start = (currentPage - 1) * this.detectionPageSize;
      return detections.slice(start, start + this.detectionPageSize);
    },
    cytomineBasePath() {
      try {
        return `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      } catch (error) {
        return '/api/';
      }
    },
    annotationForDetection(detection) {
      const centroid = detection.centroid || {
        x: Number(detection.x),
        y: Number(detection.y)
      };
      const annot = new Annotation({
        id: detection.id,
        class: detection.class,
        created: detection.created,
        updated: detection.updated,
        location: detection.location || `POINT (${Number(centroid.x)} ${Number(centroid.y)})`,
        geometryCompression: detection.geometryCompression,
        centroid,
        user: detection.user,
        term: detection.term,
        userByTerm: detection.userByTerm,
        area: detection.area,
        areaUnit: detection.areaUnit,
        perimeter: detection.perimeter,
        perimeterUnit: detection.perimeterUnit,
        image: detection.image || this.images[0]?.id,
        project: detection.project || this.project?.id,
        slice: detection.slice || this.activeSliceId
      });
      const basePath = this.cytomineBasePath();
      annot.cropURL = detection.cropURL || `${basePath}annotation/${detection.id}/crop.png`;
      annot.smallCropURL = detection.smallCropURL || `${basePath}annotation/${detection.id}/crop.png?maxSize=256`;
      annot.leprosyDetectionType = detection.type;
      annot.confidence = detection.confidence;
      return annot;
    },
    meanConfidence(type) {
      const derivedMean = this.result.derived?.meanConfidenceByType?.[type];
      if (derivedMean !== undefined) {
        return derivedMean;
      }
      const detections = this.detectionsForType(type);
      if (!detections.length) {
        return null;
      }
      return detections.reduce((sum, detection) => sum + detection.confidence, 0) / detections.length;
    },
    normalizedDetectionType() {
      return 'afb';
    },
    normalizedHeatmapPointType() {
      return 'afb';
    },
    formatPercent(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
      }
      return `${Math.round(Number(value) * 100)}%`;
    },
    formatDecimal(value) {
      return Number(value || 0).toFixed(2);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/colors.scss';

.section-title {
  padding: 0.72rem 0.85rem;
  font-weight: 700;
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(222, 238, 255, 0.045);
  color: $dark-text-primary;
  align-items: center;
  margin-top: 8px;
  transition: background-color 0.16s ease;
}

.section-title:hover {
  background-color: rgba($primary, 0.08);
}

.section-title:first-child {
  margin-top: 0;
}

.section-content {
  padding: 0.8rem 0.85rem;
  background-color: rgba(8, 17, 29, 0.18);
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  font-size: 13px;
}

.section-content .table {
  font-size: 13px;
}

.section-content .label {
  font-size: 13px !important;
  font-weight: 500;
}

.section-content .input,
.section-content .select select {
  font-size: 13px !important;
  height: 2.25em;
}

.centered-table {
  background-color: transparent !important;
  color: $dark-text-primary !important;
}

.centered-table th {
  color: $dark-text-secondary !important;
  border-color: $dark-border-color !important;
  text-align: center !important;
}

.centered-table td {
  border-color: $dark-border-color !important;
  vertical-align: middle !important;
}

.centered-table tbody tr:hover {
  background-color: rgba($primary, 0.055);
}

.centered-table th:first-child,
.centered-table td:first-child {
  text-align: left !important;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.scoring-box,
.scoring-details-box,
.leprosy-status-box,
.leprosy-term-card {
  background: rgba(222, 238, 255, 0.038);
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
}

.leprosy-status-box.is-detected {
  border-color: rgba($danger, 0.48);
  background: rgba($danger, 0.08);
}

.leprosy-status-box.is-detected .is-size-5 {
  color: #ff7896;
}

.leprosy-status-box.is-not-detected {
  border-color: rgba($success, 0.42);
  background: rgba($success, 0.07);
}

.leprosy-status-box.is-not-detected .is-size-5 {
  color: #67e09a;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked+.switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked+.switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked+.switch-slider:before {
  transform: translateX(16px);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  min-width: 12px;
  min-height: 12px;
}

.afb-dot {
  background: #ff3860;
}

.leprosy-term-card {
  margin-top: 0.85rem;
  padding: 0.75rem;
}

.leprosy-term-header {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  min-width: 0;
}

.leprosy-term-title {
  align-items: center;
  color: $dark-text-primary;
  display: flex;
  flex: 1 1 auto;
  font-weight: 600;
  min-width: 0;
}

.leprosy-term-summary {
  border-top: 1px solid rgba(190, 214, 238, 0.1);
  margin-top: 0.55rem;
  padding-top: 0.45rem;
}

.leprosy-term-summary-row {
  align-items: center;
  color: $dark-text-secondary;
  display: flex;
  font-size: 0.82rem;
  justify-content: space-between;
  line-height: 1.7;
}

.leprosy-term-summary-row strong {
  color: $dark-text-primary;
  font-weight: 600;
}

.leprosy-term-controls {
  align-items: center;
  display: flex;
  margin-top: 0.65rem;
}

.leprosy-term-threshold {
  flex: 1 1 auto;
  min-width: 0;
}

.leprosy-threshold-label {
  align-items: center;
  color: $dark-text-secondary;
  display: flex;
  font-size: 0.78rem;
  justify-content: space-between;
  margin-bottom: 0.15rem;
}

.leprosy-threshold-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: $dark-text-secondary;
  cursor: pointer;
  display: flex;
  font-size: 0.78rem;
  font-weight: 600;
  gap: 0.25rem;
  min-width: 0;
  padding: 0;
}

.leprosy-threshold-toggle:hover,
.leprosy-threshold-toggle:focus {
  color: $dark-text-primary;
}

.leprosy-threshold-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  justify-content: flex-end;
}

.leprosy-heatmap-label {
  color: $dark-text-secondary;
  font-size: 0.75rem;
  line-height: 1;
}

.leprosy-threshold-value {
  background: rgba(222, 238, 255, 0.06);
  border: 1px solid rgba(190, 214, 238, 0.13);
  border-radius: 6px;
  color: $dark-text-primary;
  font-weight: 600;
  line-height: 1;
  min-width: 3rem;
  padding: 0.22rem 0.35rem;
  text-align: center;
}

.leprosy-term-detections {
  margin-top: 0.6rem;
}

.leprosy-detection-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.55rem;
  padding-top: 0.2rem;
}

.leprosy-detection-preview {
  align-items: center;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.leprosy-detection-preview :deep(.annot-preview) {
  margin: 0;
}

.leprosy-detection-confidence {
  color: $dark-text-primary;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  margin-top: 0.35rem;
}

.leprosy-detection-empty {
  font-size: 0.8rem;
  padding: 0.65rem 0;
}

.leprosy-detection-pagination {
  margin-top: 0.75rem;
}

.leprosy-detection-pagination :deep(.pagination-list) {
  justify-content: center;
}

.algorithm-term-toggle {
  background: transparent;
  border: none;
  color: $dark-text-secondary;
  cursor: pointer;
  margin-right: 0.25rem;
  padding: 0;
}

.algorithm-term-toggle:hover {
  color: $dark-text-primary;
}

.term-arrow {
  display: inline-block;
  min-width: 1rem;
  text-align: center;
}

.leprosy-term-annotation-switch {
  flex: 0 0 auto;
  margin-right: 0.25rem;
}

.leprosy-term-heatmap-switch,
.leprosy-term-confidence-label-switch {
  flex: 0 0 auto;
  margin-right: 0.25rem;
}

.leprosy-threshold-actions .leprosy-term-heatmap-switch,
.leprosy-threshold-actions .leprosy-term-confidence-label-switch {
  margin-right: 0;
}

.report-form .field-label {
  text-align: left;
  flex: 0 0 110px;
  max-width: 110px;
  padding-top: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.report-form .field-label.report-label-wide {
  flex: 0 0 160px;
  max-width: 160px;
}

.report-form .field-body {
  flex: 1 1 auto;
  min-width: 0;
  align-items: center !important;
  display: flex !important;
}

.report-form .control {
  display: flex !important;
  align-items: center !important;
  gap: 5px;
}

.report-form .field {
  margin-bottom: 0.65rem !important;
}

.report-form .control.report-control-fill {
  width: 100%;
}

.align-items-center {
  align-items: center;
}

.printed-report-preview,
.activity-log-entry {
  color: $dark-text-primary;
  display: grid;
  gap: 0.45rem;
}
</style>
